import { useEffect, useState, useRef } from "react";
import { useDispatchApp } from "../lib/redux";
import { startGetStatisticsAdsCampaigns } from "../actions/adsCampaigns";
import { ADS_CAMPAIGN_STATUS_DRAFT } from "../utils/constants";

import { LaunchError } from "../utils/errors";

/**
 * Custom hook to handle campaign statistics with cache by date range
 * @param {Object} campaigns - Object with available campaigns
 * @param {Object} dateRange - Current date range { startDateTime, endDateTime }
 * @returns {Object} - { statistics, statisticsLoading, fetchStatistics }
 */
const useStatisticsWithDateRange = (campaigns, dateRange) => {
  const dispatch = useDispatchApp();
  // Statistics cache by date range
  // Structure: { "startDate-endDate": { campaignID: statistics } }
  const [statisticsCache, setStatisticsCache] = useState({});

  // Previous period statistics cache by date range
  // Structure: { "startDate-endDate": { campaignID: periodLastStatistics } }
  const [previousPeriodCache, setPreviousPeriodCache] = useState({});

  // Array of campaign IDs currently being queried
  const [statisticsLoading, setStatisticsLoading] = useState([]);

  // Reference to avoid duplicate calls
  const fetchingRef = useRef(false);

  /**
   * Generates cache key based on date range
   */
  const generateCacheKey = (startDateTime, endDateTime) => {
    if (!startDateTime && !endDateTime) {
      return "no-filter";
    }
    return `${startDateTime || "null"}-${endDateTime || "null"}`;
  };

  /**
   * Gets statistics for the current date range
   */
  const getCurrentStatistics = () => {
    const cacheKey = generateCacheKey(
      dateRange?.startDateTime,
      dateRange?.endDateTime
    );
    return statisticsCache[cacheKey] || {};
  };

  /**
   * Gets previous period statistics for the current date range
   */
  const getCurrentPreviousPeriodStatistics = () => {
    const cacheKey = generateCacheKey(
      dateRange?.startDateTime,
      dateRange?.endDateTime
    );
    const result = previousPeriodCache[cacheKey] || {};
    return result;
  };

  const hasExplicitRange = !!(
    dateRange?.startDateTime && dateRange?.endDateTime
  );

  const getCampaignsToFetch = () => {
    if (!campaigns || !hasExplicitRange) {
      return [];
    }

    const currentStats = getCurrentStatistics();
    const campaignIDs = Object.keys(campaigns);

    // Filter campaigns that are not in current cache and not being queried and that are not draft
    return campaignIDs
      .filter((campaignID) => {
        const campaign = campaigns[campaignID];
        return campaign?.states?.general !== ADS_CAMPAIGN_STATUS_DRAFT;
      })
      .filter(
        (campaignID) =>
          !currentStats[campaignID] && !statisticsLoading.includes(campaignID)
      );
  };

  /**
   * Main function to query statistics
   */
  const fetchStatistics = async (campaignIDs = null) => {
    if (fetchingRef.current) return;

    const targetCampaignIDs = campaignIDs || getCampaignsToFetch();

    if (targetCampaignIDs.length === 0) {
      return;
    }

    fetchingRef.current = true;

    // Add campaigns to loading state
    setStatisticsLoading((prev) => [
      ...new Set([...prev, ...targetCampaignIDs]),
    ]);

    try {
      const response = await dispatch(
        startGetStatisticsAdsCampaigns({
          campaignIDs: targetCampaignIDs,
          startDateTime: dateRange?.startDateTime,
          endDateTime: dateRange?.endDateTime,
        })
      );

      if (response.ok) {
        const cacheKey = generateCacheKey(
          dateRange?.startDateTime,
          dateRange?.endDateTime
        );

        // Update cache with new statistics
        setStatisticsCache((prev) => ({
          ...prev,
          [cacheKey]: {
            ...prev[cacheKey],
            ...response.data,
          },
        }));

        // Update previous period cache if periodLast data is available
        setPreviousPeriodCache((prev) => ({
          ...prev,
          [cacheKey]: {
            ...prev[cacheKey],
            ...response.periodLast,
          },
        }));
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "fetchStatistics",
          capture: "exception",
        })
      );
      console.error("Error fetching statistics:", error);
    } finally {
      // Remove campaigns from loading state
      setStatisticsLoading((prev) =>
        prev.filter((id) => !targetCampaignIDs.includes(id))
      );
      fetchingRef.current = false;
    }
  };

  /**
   * Clears cache and resets state when date range changes
   */
  const handleDateRangeChange = () => {
    // Cancel any ongoing queries
    setStatisticsLoading([]);
    fetchingRef.current = false;

    if (hasExplicitRange && campaigns) {
      const campaignsToFetch = getCampaignsToFetch();
      if (campaignsToFetch.length > 0) {
        fetchStatistics(campaignsToFetch);
      }
    }
  };

  const handleNewCampaigns = () => {
    if (hasExplicitRange && campaigns) {
      const campaignsToFetch = getCampaignsToFetch();
      if (campaignsToFetch.length > 0) {
        fetchStatistics(campaignsToFetch);
      }
    }
  };

  // Effect to detect changes in date range
  useEffect(() => {
    handleDateRangeChange();
    // eslint-disable-next-line
  }, [dateRange?.startDateTime, dateRange?.endDateTime]);

  // Effect to detect new campaigns (pagination)
  useEffect(() => {
    if (campaigns && Object.keys(campaigns).length > 0) {
      handleNewCampaigns();
    }
    // eslint-disable-next-line
  }, [campaigns]);

  return {
    // Statistics for the current date range
    statistics: getCurrentStatistics(),

    // Previous period statistics for the current date range
    previousPeriodStatistics: getCurrentPreviousPeriodStatistics(),

    // Array of campaign IDs being queried
    statisticsLoading,

    // Function to force statistics query
    fetchStatistics: () => fetchStatistics(),

    // Check if there is an active date range
    hasActiveDateRange: hasExplicitRange,

    // Complete cache (for debugging if necessary)
    statisticsCache,
  };
};

export default useStatisticsWithDateRange;
